import React, { useMemo, useEffect, useRef } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';
import { useApp } from '../../contexts/AppContext';
import { calculateSingleProperty, formatValue } from '../../utils';

const PortfolioCharts: React.FC = () => {
  const { portfolio } = useApp();
  const chartRefs = useRef<{ [key: string]: ChartJS | null }>({});

  // Cleanup chart instances on unmount
  useEffect(() => {
    // Capture refs for cleanup
    const charts = chartRefs.current;
    
    return () => {
      Object.values(charts).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, []);

  const portfolioData = useMemo(() => {
    return portfolio.map(property => ({
      ...property,
      calculations: calculateSingleProperty(property.inputs)
    }));
  }, [portfolio]);

  if (portfolio.length === 0) {
    return null;
  }

  // Chart Colors
  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
  ];

  // Cash Flow Comparison
  const cashflowData = {
    labels: portfolioData.map(p => p.name),
    datasets: [{
      label: 'Monatlicher Cashflow nach Steuern',
      data: portfolioData.map(p => p.calculations.cashflowMonatlichNachSteuer),
      backgroundColor: portfolioData.map((p, i) => 
        p.calculations.cashflowMonatlichNachSteuer < 0 
          ? 'rgba(239, 68, 68, 0.5)' 
          : colors[i % colors.length]
      ),
      borderColor: portfolioData.map((p, i) => 
        p.calculations.cashflowMonatlichNachSteuer < 0 
          ? '#EF4444' 
          : colors[i % colors.length]
      ),
      borderWidth: 1
    }]
  };

  // ROI Comparison
  const roiData = {
    labels: portfolioData.map(p => p.name),
    datasets: [{
      label: 'Eigenkapitalrendite (%)',
      data: portfolioData.map(p => p.calculations.eigenkapitalrendite),
      backgroundColor: 'rgba(16, 185, 129, 0.5)',
      borderColor: '#10B981',
      borderWidth: 2
    }]
  };

  // Investment Distribution
  const investmentData = {
    labels: portfolioData.map(p => p.name),
    datasets: [{
      data: portfolioData.map(p => p.calculations.gesamtkosten),
      backgroundColor: colors.slice(0, portfolioData.length),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // 10-Year Wealth Projection
  const wealthProjectionData = {
    labels: portfolioData.map(p => p.name),
    datasets: [
      {
        label: 'Eigentumsanteil',
        data: portfolioData.map(p => p.calculations.projectionData[10]?.eigentumsanteil || 0),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: '#3B82F6',
        borderWidth: 2
      },
      {
        label: 'Nettovermögen',
        data: portfolioData.map(p => p.calculations.projectionData[10]?.vermoegen || 0),
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: '#10B981',
        borderWidth: 2
      }
    ]
  };

  // Total Portfolio Performance Over Time
  const years = Array.from({ length: 21 }, (_, i) => i);
  const totalPortfolioData = {
    labels: years,
    datasets: [
      {
        label: 'Gesamtes Nettovermögen',
        data: years.map(year => 
          portfolioData.reduce((sum, p) => 
            sum + (p.calculations.projectionData[year]?.vermoegen || 0), 0
          )
        ),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Gesamter Eigentumsanteil',
        data: years.map(year => 
          portfolioData.reduce((sum, p) => 
            sum + (p.calculations.projectionData[year]?.eigentumsanteil || 0), 0
          )
        ),
        borderColor: '#10B981',
        borderDash: [5, 5],
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatValue(context.parsed.y, label.includes('%') ? '%' : '€');
            }
            return label;
          }
        }
      }
    }
  };

  const barOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => formatValue(value, '€')
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = formatValue(context.parsed, '€');
            const percentage = ((context.parsed / context.dataset.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="mt-8 space-y-8">
      <h3 className="text-xl font-semibold">Portfolio Analyse</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Cashflow Comparison */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold mb-4">Cashflow Vergleich</h4>
          <div style={{ height: '300px' }}>
            <Bar 
              data={cashflowData} 
              options={barOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['cashflow'] = ref as unknown as ChartJS;
                }
              }}
            />
          </div>
        </div>

        {/* ROI Comparison */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold mb-4">Eigenkapitalrendite Vergleich</h4>
          <div style={{ height: '300px' }}>
            <Bar 
              data={roiData}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['roi'] = ref as unknown as ChartJS;
                }
              }}
              options={{
              ...chartOptions,
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (value: any) => formatValue(value, '%')
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* Investment Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold mb-4">Investitionsverteilung</h4>
          <div style={{ height: '300px' }}>
            <Doughnut 
              data={investmentData} 
              options={doughnutOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['investment'] = ref as unknown as ChartJS;
                }
              }}
            />
          </div>
        </div>

        {/* 10-Year Wealth */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold mb-4">Vermögen nach 10 Jahren</h4>
          <div style={{ height: '300px' }}>
            <Bar 
              data={wealthProjectionData} 
              options={barOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['wealthProjection'] = ref as unknown as ChartJS;
                }
              }}
            />
          </div>
        </div>

        {/* Total Portfolio Performance */}
        <div className="bg-white p-6 rounded-lg shadow-md lg:col-span-2">
          <h4 className="text-lg font-semibold mb-4">Gesamtportfolio Entwicklung</h4>
          <div style={{ height: '400px' }}>
            <Line 
              data={totalPortfolioData}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['totalPortfolio'] = ref as unknown as ChartJS;
                }
              }}
              options={{
              ...chartOptions,
              scales: {
                x: {
                  title: {
                    display: true,
                    text: 'Jahr'
                  }
                },
                y: {
                  title: {
                    display: true,
                    text: 'Wert (€)'
                  },
                  ticks: {
                    callback: (value: any) => formatValue(value, '€')
                  }
                }
              }
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioCharts;